import { apiRoutes, apiUrl, extractApiList } from './api'
import { selectRequestedFarms } from './feedInventoryAccess'
import { lookupBrfBarn, resolveBarnLocation } from './brfBarnCoordinates'
import {
  barnPriority,
  historyWindow,
  normalizeHistoryPayload,
  saoPauloToday,
} from './feedConsumption'
import { fetchSiloProfileData, resolveMaxWeight } from './feedInventory'

const POOL_SIZE = 8

export async function mapPool(items, limit, worker) {
  const results = new Array(items.length)
  let cursor = 0
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor
      cursor += 1
      results[index] = await worker(items[index], index)
    }
  })
  await Promise.all(runners)
  return results
}

const siloPkOf = (sensor) => {
  const silo = sensor?.silo
  if (silo == null) return null
  if (typeof silo === 'object') return silo.pk ?? silo.id ?? null
  return silo
}

const historySensorPk = (sensor) => sensor?.sensor ?? sensor?.pk ?? sensor?.id ?? null

const isVirtualSensor = (sensor) => (
  sensor?.virtual === true || sensor?.virtual === 'true' || sensor?.virtual === 1
)

async function getJson(url, headers) {
  const response = await fetch(url, { headers })
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }
  return response.json()
}

function dedupeSmartfeed(sensors) {
  const bySilo = new Map()
  for (const sensor of sensors || []) {
    if (sensor?.type !== 'smartfeed' || isVirtualSensor(sensor)) continue
    const siloPk = siloPkOf(sensor)
    if (siloPk == null || bySilo.has(String(siloPk))) continue
    const informed = Number(sensor.informed_max_weight ?? sensor.silo?.informed_max_weight ?? 0) || 0
    bySilo.set(String(siloPk), {
      id: siloPk,
      sensorPk: historySensorPk(sensor),
      name: sensor.name || null,
      informedMaxWeight: informed,
      weight: 0,
      level: 0,
      maxWeight: 0,
      percentage: null,
      readingDate: null,
      history: [],
      rawSensor: sensor,
    })
  }
  return [...bySilo.values()]
}

async function fillFarmNames(farms, headers) {
  await Promise.all(farms.map(async (farm) => {
    if (farm.name) return
    try {
      const data = await getJson(apiUrl(`farms/${farm.pk}/`), headers)
      farm.name = data?.name || data?.farm_name || ''
    } catch (error) {
      farm.name = ''
    }
  }))
}

async function loadBarnPack(companyId, farm, barn, headers) {
  const barnPk = barn?.pk ?? barn?.id
  if (barnPk == null) return null
  let sensors = []
  try {
    const payload = await getJson(apiRoutes.barnSensorsHome(barnPk), headers)
    sensors = extractApiList(payload)
  } catch (error) {
    console.error('Feed inventory: error loading barn sensors', barnPk, error)
    return null
  }
  const silos = dedupeSmartfeed(sensors)
  if (!silos.length) return null
  const csv = lookupBrfBarn(companyId, barnPk)
  return {
    barn,
    farm,
    silos,
    location: resolveBarnLocation(companyId, barn),
    csv,
  }
}

async function loadSiloSnapshot(silo, headers) {
  const profile = await fetchSiloProfileData(headers, silo.id)
  let informed = Number(silo.informedMaxWeight || profile?.informed_max_weight || 0) || 0
  if (informed <= 0) {
    try {
      const cadastro = await getJson(apiUrl(`silos/${silo.id}/`), headers)
      informed = Number(cadastro?.informed_max_weight || 0) || 0
    } catch (error) {
      informed = 0
    }
  }
  const weight = Number(profile?.weight ?? 0) || 0
  const level = Number(profile?.level ?? 0) || 0
  const maxWeight = resolveMaxWeight({
    last_hour_weight: weight,
    weight,
    last_hour_level: level,
    level,
    informed_max_weight: informed,
  })
  const percentage = maxWeight > 0
    ? Math.round((weight / maxWeight) * 100)
    : (level > 0 ? Math.round(level) : null)
  return {
    name: profile?.silo_name || silo.name || `Silo ${silo.id}`,
    weight,
    level,
    maxWeight,
    percentage,
    readingDate: profile?.datetime_fetched || profile?.last_updated || null,
  }
}

function historyDateTime(value, endOfDay) {
  const raw = String(value || '').trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return endOfDay ? `${raw}T23:59:59` : `${raw}T00:00:00`
  }
  return raw
}

async function loadSiloHistory(silo, headers, start, end) {
  const sensorPk = silo?.sensorPk ?? silo?.id
  if (sensorPk == null) return []
  try {
    const response = await fetch(apiUrl('silo_profiles/silo_profile_history/?include_curves=false'), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        sensor_pk: sensorPk,
        start_date: historyDateTime(start, false),
        end_date: historyDateTime(end, true),
        bypass_validation: true,
      }),
    })
    if (!response.ok) return []
    const payload = await response.json()
    return normalizeHistoryPayload(payload)
  } catch (error) {
    console.error('Feed inventory: error loading silo history', sensorPk, error)
    return []
  }
}

function buildRow(companyName, pack, today, historyReady) {
  const silos = pack.silos.map((silo) => ({
    id: silo.id,
    name: silo.name,
    lastWeight: silo.weight || 0,
    lastFetched: silo.readingDate,
    history: historyReady ? (silo.history || []) : [],
    maxWeight: silo.maxWeight || 0,
    level: silo.level || 0,
    percentage: silo.percentage,
    readingDate: silo.readingDate,
    rawSensor: silo.rawSensor,
  }))
  const producer = pack.farm.name || pack.csv?.producer || pack.location.producer || 'Produtor'
  const priority = barnPriority({
    unit: companyName,
    producer,
    barnName: pack.barn.name || `Galpão ${pack.barn.pk ?? pack.barn.id}`,
    silos,
  }, today)
  const capacity = silos.reduce((sum, silo) => sum + (silo.maxWeight || 0), 0)
  const percentage = capacity > 0 ? Math.round((priority.stock / capacity) * 100) : 0
  return {
    ...priority,
    historyReady,
    barnPk: pack.barn.pk ?? pack.barn.id,
    barn: pack.barn,
    farmPk: pack.farm.pk,
    capacity,
    percentage,
    municipality: pack.location.municipality || pack.csv?.municipality || '',
    uf: pack.csv?.uf || '',
    lat: pack.location.lat,
    lng: pack.location.lng,
    coordSource: pack.location.source,
    silos: silos.map((silo) => ({
      id: silo.id,
      name: silo.name,
      weight: silo.lastWeight,
      maxWeight: silo.maxWeight,
      level: silo.level,
      percentage: silo.percentage,
      readingDate: silo.readingDate,
      rawSensor: silo.rawSensor,
    })),
  }
}

async function processBarn({ companyId, companyName, farm, barn, headers, today, range, onBarn }) {
  const pack = await loadBarnPack(companyId, farm, barn, headers)
  if (!pack) return

  await Promise.all(pack.silos.map(async (silo) => {
    const snapshot = await loadSiloSnapshot(silo, headers)
    if (snapshot) Object.assign(silo, snapshot)
  }))
  if (onBarn) onBarn({ companyName, row: buildRow(companyName, pack, today, false) })

  await Promise.all(pack.silos.map(async (silo) => {
    silo.history = await loadSiloHistory(silo, headers, range.start, range.end)
  }))
  if (onBarn) onBarn({ companyName, row: buildRow(companyName, pack, today, true) })
}

export async function loadCompanyFeedInventory({ companyId, headers, onBarn, onProgress, farmPk = null, farmPks = null }) {
  let companyName = `Unidade ${companyId}`
  let farms = []
  try {
    const company = await getJson(apiUrl(`companies/${companyId}/`), headers)
    companyName = company?.name || companyName
    farms = extractApiList(company?.farms)
  } catch (error) {
    const hasFarmLimit = farmPk != null || (Array.isArray(farmPks) && farmPks.length > 0)
    if (!hasFarmLimit) throw error
  }

  const limit = Array.isArray(farmPks) && farmPks.length
    ? farmPks
    : (farmPk != null ? [farmPk] : null)
  farms = selectRequestedFarms(farms, limit)

  await fillFarmNames(farms, headers)
  if (!farms.length) return { companyName }

  const today = saoPauloToday()
  const range = historyWindow(today)
  const seen = new Set()
  const queue = []
  let active = 0
  let done = 0
  let farmsSettled = 0
  let resolveIdle
  const idle = new Promise((resolve) => {
    resolveIdle = resolve
  })

  const report = () => {
    if (typeof onProgress !== 'function') return
    const known = farmsSettled === farms.length
    onProgress({
      total: known ? seen.size : 0,
      done,
      known,
    })
  }

  const finishIfIdle = () => {
    if (farmsSettled === farms.length && active === 0 && queue.length === 0) resolveIdle()
  }

  const pump = () => {
    while (active < POOL_SIZE && queue.length) {
      const job = queue.shift()
      active += 1
      processBarn(job).finally(() => {
        done += 1
        report()
        active -= 1
        pump()
        finishIfIdle()
      })
    }
    finishIfIdle()
  }

  await Promise.all(farms.map(async (farm) => {
    let barns = []
    try {
      const payload = await getJson(apiRoutes.farmHomeByFarm(farm.pk), headers)
      barns = extractApiList(payload)
    } catch (error) {
      console.error('Feed inventory: error loading farm barns', farm.pk, error)
    }
    barns.forEach((barn) => {
      const barnPk = barn?.pk ?? barn?.id
      if (barnPk == null || seen.has(String(barnPk))) return
      seen.add(String(barnPk))
      queue.push({
        companyId,
        companyName,
        farm,
        barn,
        headers,
        today,
        range,
        onBarn,
      })
    })
    farmsSettled += 1
    report()
    pump()
  }))

  await idle
  return { companyName }
}
